/**
 * 404 Not Found page.
 *
 * Playful, on-brand page with an animated Morgan logo
 * and a gentle nudge back to safety. Works in light & dark mode.
 */

import { Link } from "@tanstack/react-router";
import { Logo } from "@/ui/layout/logo";
import { Button } from "@/ui/primitives/button";
import { ArrowLeft, Home } from "lucide-react";

export function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center">
      {/* Logo */}
      <div className="relative mb-8">
        <div className="absolute inset-0 -m-8 rounded-full bg-primary/5 blur-2xl dark:bg-primary/10" />
        <div className="relative">
          <Logo
            compact
            width={80}
            height={82}
            className="text-primary dark:text-emerald-400"
          />
        </div>
      </div>

      {/* Big friendly 404 */}
      <h1 className="select-none text-[8rem] font-black leading-none tracking-tighter text-primary/10 dark:text-white/5 sm:text-[10rem]">
        404
      </h1>

      {/* Message */}
      <div className="-mt-6 space-y-3">
        <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">
          Page not found
        </h2>
        <p className="mx-auto max-w-md text-sm leading-relaxed text-muted-foreground">
          Looks like this page has been liquidated. Don&apos;t worry — unlike
          some of the companies we investigate, your data is perfectly safe.
        </p>
      </div>

      {/* Actions */}
      <div className="mt-8 flex items-center gap-3">
        <Button variant="outline" size="sm" asChild>
          <Link to="/" className="gap-1.5">
            <ArrowLeft className="h-4 w-4" />
            Go back
          </Link>
        </Button>
        <Button size="sm" asChild>
          <Link to="/" className="gap-1.5">
            <Home className="h-4 w-4" />
            Dashboard
          </Link>
        </Button>
      </div>

      {/* Decorative bottom bar — echoes the Morgan bar-chart motif */}
      <div className="mt-16 flex items-end gap-1.5">
        {[20, 32, 44, 56, 44, 32, 20].map((h, i) => (
          <div
            key={i}
            className="w-2 rounded-sm bg-primary/10 dark:bg-white/5"
            style={{ height: `${h}px` }}
          />
        ))}
      </div>
    </div>
  );
}
